import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import CustomOverlayComponent from 'components/core/library/mapV2/customOverlayComponent';
import { Text } from 'components/core/Text';
import withViewportWidth from 'decorators/withViewportWidth';
import FacilityInfoWindow from './FacilityInfoWindow';

function withFacilityMarkerLogic(WrappedComponent) {
	function WithFacilityMarkerLogic(props) {
		const [active, setActiveState] = React.useState(false);
		const [infoWindowOpen, setInfoWindowOpen] = React.useState(false);
		const [zIndex, setZIndex] = React.useState(props.zIndex);

		const setActive = (isActive) => {
			setActiveState(isActive);
			setZIndex(isActive ? 2000 : props.zIndex);
		};

		const handleMarkerClick = () => {
			setInfoWindowOpen((prev) => !prev);
			setZIndex(2000);
		};

		const handlePageClick = () => setInfoWindowOpen(false);

		// Sync active state with highlighted prop
		React.useEffect(() => {
			if (active !== !!props.highlighted) {
				setActive(!!props.highlighted);
			}
		}, [props.highlighted]);

		const label = React.useMemo(() => {
			const { facility } = props;
			if (facility.cheapestPrice && facility.cheapestPrice.text) {
				return `$${facility.cheapestPrice.text.split('.')[0]}`;
			}

			const cheapestUnitPrice = facility.units.reduce(
				(min, unit) =>
					parseInt(unit.price?.text) < parseInt(min)
						? unit.price?.text
						: min,
				facility.units[0].price?.text,
			);

			return `$${cheapestUnitPrice?.split('.')[0]}`;
		}, [props.facility]);

		React.useEffect(() => {
			if (props.isPreselectedFacility) {
				handleMarkerClick();
			}
			return () => {
				handlePageClick();
			};
		}, []);

		return (
			<WrappedComponent
				{...props}
				active={active}
				infoWindowOpen={infoWindowOpen}
				zIndex={zIndex}
				setActive={setActive}
				handleMarkerClick={handleMarkerClick}
				handlePageClick={handlePageClick}
				label={label}
			/>
		);
	}
	return WithFacilityMarkerLogic;
}

export const enhance = (Component) =>
	withViewportWidth(withFacilityMarkerLogic(Component));

export function FacilityMarker({
	position, label, setActive, active, zIndex, facility, handleMarkerClick, infoWindowOpen, handlePageClick,
	phone, viewportWidth, getLocalPhoneNumberByZipCode
}) {
	return (
		<div>
			<CustomOverlayComponent
				position={position}
				zIndex={zIndex}
			>
				<button
					className={classnames('facility-marker-icon', { active, hidden: infoWindowOpen })}
					onMouseEnter={() => setActive(true)}
					onMouseOut={() => setActive(false)}
					onBlur={() => setActive(false)}
					onClick={handleMarkerClick}
					type="button"
				>
					<Text className="marker-label">{label}</Text>
				</button>
			</CustomOverlayComponent>
			{!!infoWindowOpen &&
				<FacilityInfoWindow
					idleDisabled
					viewportWidth={viewportWidth}
					position={position}
					facility={facility}
					onClickOutside={handlePageClick}
					phone={phone}
					getLocalPhoneNumberByZipCode={getLocalPhoneNumberByZipCode}
				/>
			}
		</div>
	);
}

FacilityMarker.propTypes = {
	position: PropTypes.shape({ lat: PropTypes.number, lng: PropTypes.number }),
	label: PropTypes.string,
	setActive: PropTypes.func,
	active: PropTypes.bool,
	zIndex: PropTypes.number,
	facility: PropTypes.object,
	handleMarkerClick: PropTypes.func,
	infoWindowOpen: PropTypes.bool,
	handlePageClick: PropTypes.func,
	phone: PropTypes.object,
	getLocalPhoneNumberByZipCode: PropTypes.func,
	viewportWidth: PropTypes.number
};

export default enhance(FacilityMarker);
